import { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { IconPlus, IconPencil, IconTrash } from '@tabler/icons-react';
import { AlertMessage, Button, IconButton, Loader } from 'ui';
import { getNetworkError } from 'common/utils';

import {
  getContactEvents,
  createContactActivity,
  updateContactActivity,
  deleteContactActivity,
} from '../../api/contacts';
import { getCampaignNameMap } from '../../api/campaigns';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import ActivityComposer from './ActivityComposer';
import { getEntryType, groupEventsByDay, formatReason, formatUrl, isManual } from './contact-activity-utils';

const PAGE_SIZE = 20;

const ActivityItem = (props) => {
  const { entry, campaignName, isLast, onEdit, onDelete } = props;
  const { Icon, label, className } = getEntryType(entry);
  const manual = isManual(entry);

  const detail = manual
    ? [entry.createdBy ? `Logged by ${entry.createdBy}` : null].filter(Boolean).join(' · ')
    : [campaignName, entry.device, formatReason(entry.reason)].filter(Boolean).join(' · ');

  return (
    <li className="relative flex gap-3 pb-4 last:pb-0" data-testid="contact-activity-item" data-source={entry.source}>
      {!isLast && <span aria-hidden="true" className="absolute bottom-0 left-4 top-8 w-px bg-gray-50" />}

      <span className={`flex size-8 shrink-0 items-center justify-center rounded-full ${className}`}>
        <Icon size={16} aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1 pt-1">
        <p className="font-medium text-gray-950">{label}</p>

        {!!detail && <p className="text-sm text-gray-600">{detail}</p>}

        {!!entry.notes && <p className="text-sm text-gray-700">{entry.notes}</p>}

        {!!entry.url && (
          <a
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            title={entry.url}
            className="block truncate text-sm text-primary-700 hover:underline"
          >
            {formatUrl(entry.url)}
          </a>
        )}
      </div>

      {/* Only manual entries are editable — that asymmetry is what tells a user
          which rows are theirs rather than reported by the email provider.
          Always rendered rather than hover-only: a hover-only control is
          undiscoverable with a mouse until you stumble on it, and absent
          entirely on touch. Low contrast at rest, darkening on hover. */}
      {manual && (
        <div className="flex shrink-0 items-start gap-0.5 pt-0.5">
          <IconButton
            Icon={<IconPencil size={16} />}
            srOnly={`Edit ${label}`}
            color="transparent"
            className="!p-1 !text-gray-300 hover:!text-gray-700"
            onClick={() => onEdit(entry)}
          />
          <IconButton
            Icon={<IconTrash size={16} />}
            srOnly={`Delete ${label}`}
            color="transparent"
            className="!p-1 !text-gray-300 hover:!text-red-500"
            onClick={() => onDelete(entry)}
          />
        </div>
      )}

      <time dateTime={entry.timestamp} className="shrink-0 pt-1.5 text-sm text-gray-400">
        {format(new Date(entry.timestamp), 'h:mm a')}
      </time>
    </li>
  );
};

ActivityItem.propTypes = {
  entry: PropTypes.object.isRequired,
  campaignName: PropTypes.string,
  isLast: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const ContactActivity = (props) => {
  const { contactId } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [campaignNames, setCampaignNames] = useState({});

  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState('');

  const loadEvents = useCallback(async (id, cursor) => {
    setLoading(true);
    setError('');
    try {
      const page = await getContactEvents(id, { cursor, limit: PAGE_SIZE });
      // Append when paging, replace when loading the first page.
      setEvents((current) => (cursor ? [...current, ...page.events] : page.events));
      setNextCursor(page.nextCursor);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setEvents([]);
    setNextCursor(null);
    setComposerOpen(false);
    setEditing(null);
    loadEvents(contactId);
  }, [contactId, loadEvents]);

  useEffect(() => {
    let active = true;
    getCampaignNameMap().then((names) => {
      if (active) setCampaignNames(names);
    });
    return () => {
      active = false;
    };
  }, []);

  const closeComposer = () => {
    setComposerOpen(false);
    setEditing(null);
  };

  /**
   * Reload the first page after a write. An edited timestamp can move an entry
   * anywhere in the timeline, so patching it in place would be wrong — and the
   * cursor for the pages already loaded would no longer line up.
   */
  const reload = async () => {
    setNextCursor(null);
    await loadEvents(contactId);
  };

  const handleSave = async (values) => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateContactActivity(contactId, editing.id, values);
        setToast('Activity updated.');
      } else {
        await createContactActivity(contactId, values);
        setToast('Activity logged.');
      }
      closeComposer();
      await reload();
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteContactActivity(contactId, deleteData.id);
      setDeleteData(null);
      setToast('Activity deleted.');
      await reload();
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setDeleting(false);
  };

  const handleEdit = (entry) => {
    setEditing(entry);
    setComposerOpen(true);
  };

  const days = useMemo(() => groupEventsByDay(events), [events]);
  const isInitialLoading = loading && !events.length;
  const isEmpty = !loading && !error && !events.length;

  return (
    <div className="space-y-2" data-testid="contact-activity">
      <div className="flex items-center justify-between gap-2">
        {/* The sort hint sits next to the heading, not at the right edge where
            controls live, so it reads as a caption rather than a sort toggle. */}
        <div className="flex items-baseline gap-2">
          <h3 className="text-lg font-semibold">Activity</h3>

          {!!events.length && <span className="text-sm text-gray-500">Newest first</span>}
        </div>

        {!composerOpen && (
          <Button
            title="Log activity"
            size="sm"
            LeftIcon={IconPlus}
            iconProps={{ size: 16 }}
            onClick={() => {
              setEditing(null);
              setComposerOpen(true);
            }}
          />
        )}
      </div>

      {composerOpen && (
        <ActivityComposer
          // Remount when switching between logging a new entry and editing an
          // existing one, so the form seeds from initialValues rather than being
          // re-seeded by an effect that would race the user's own edits.
          key={editing?.id ?? 'new'}
          entry={editing ?? undefined}
          saving={saving}
          onSave={handleSave}
          onCancel={closeComposer}
        />
      )}

      {isEmpty && (
        <p className="py-2 text-base text-gray-600">
          No activity yet. Log a call or meeting above, and email opens, clicks and delivery updates appear here once
          this contact receives a campaign.
        </p>
      )}

      {days.map((day, dayIndex) => (
        <div key={day.label}>
          <p className="mb-2 mt-4 text-xs font-semibold uppercase text-gray-500 first:mt-1">{day.label}</p>

          <ul className="list-none">
            {day.events.map((entry, eventIndex) => (
              <ActivityItem
                key={entry.id}
                entry={entry}
                campaignName={entry.campaignId ? campaignNames[entry.campaignId] : undefined}
                // The connector line stops at the last row of the last day so it
                // doesn't dangle into the day heading below.
                isLast={dayIndex === days.length - 1 && eventIndex === day.events.length - 1}
                onEdit={handleEdit}
                onDelete={setDeleteData}
              />
            ))}
          </ul>
        </div>
      ))}

      {!!error && <p className="py-2 text-center text-base text-red-500">{error}</p>}

      {isInitialLoading && <Loader />}

      {!!nextCursor && (
        <Button
          title="Load older activity"
          size="sm"
          loading={loading}
          onClick={() => loadEvents(contactId, nextCursor)}
          className="w-full"
        />
      )}

      <ConfirmDeleteModal
        data={deleteData ? { id: deleteData.id, name: getEntryType(deleteData).label, entity: 'activity' } : null}
        onClose={() => setDeleteData(null)}
        onDelete={handleDelete}
        loading={deleting}
      />

      <AlertMessage open={!!toast} message={toast} severity="success" onClose={() => setToast('')} />
    </div>
  );
};

ContactActivity.propTypes = {
  contactId: PropTypes.string.isRequired,
};

export default ContactActivity;
